import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import {
  PATIENT_DOCUMENTS_CONTROLER_URL,
  PATIENT_DOCUMENTS_UPLOAD,
} from "api/Service";
import { useDocumentTypes } from "features/documents/hooks/useDocumentTypes";

// Document type names the backend already seeds. Filing the generated PDF
// under "RECEIPTS" means it also shows up correctly on the Documents tab,
// not just as a one-off email attachment; OTHER_DOCUMENT is the fallback if
// a tenant hasn't got that type configured.
const RECEIPTS_DOCUMENT_TYPE_NAME = "RECEIPTS";
const FALLBACK_DOCUMENT_TYPE_NAME = "OTHER_DOCUMENT";

// Payments Due / Patient Payments have no document of their own to email —
// a statement or receipts PDF is generated on demand from payment ids, and
// the standard send-email flow only knows how to attach things by document
// id (see PatientDocumentsPage's SendEmailModal). So before that modal can
// be reused here, the generated PDF has to be saved as a real document
// first; this hook does that and hands back the id/name it can be attached
// with.
export const useCreatePaymentEmailAttachment = (patientId) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const queryClient = useQueryClient();
  const { data: documentTypes = [] } = useDocumentTypes();

  return useMutation({
    mutationFn: async ({ paymentIds, name }) => {
      if (!paymentIds?.length) {
        throw new Error("No payments selected");
      }

      const documentTypeId = (
        documentTypes.find((type) => type.name === RECEIPTS_DOCUMENT_TYPE_NAME) ||
        documentTypes.find((type) => type.name === FALLBACK_DOCUMENT_TYPE_NAME)
      )?.id;
      if (!documentTypeId) {
        throw new Error("No document type available to file the attachment under");
      }

      const axiosInstance = createAxiosInstance();

      const pdfResponse = await axiosInstance.post(
        `${tenantVal}/consultationpayment/receipt/getbyids`,
        paymentIds,
        { responseType: "blob" }
      );
      const file = new File([pdfResponse.data], `${name}.pdf`, {
        type: "application/pdf",
      });

      const uploadData = new FormData();
      uploadData.append("file", file);
      uploadData.append("name", name);

      const uploadResponse = await axiosInstance.post(
        `${tenantVal}${PATIENT_DOCUMENTS_UPLOAD}/${documentTypeId}/patient/${patientId}`,
        uploadData
      );
      const documentId = uploadResponse.data?.id;
      const documentUrl = uploadResponse.data?.documentUrl;

      await axiosInstance.post(`${tenantVal}${PATIENT_DOCUMENTS_CONTROLER_URL}`, {
        id: documentId,
        tenant: tenantVal,
        patientId,
        patient: { id: patientId, tenant: tenantVal },
        documentTypeId,
        documentTypes: { id: documentTypeId, tenant: tenantVal },
        status: "SYSTEM_GENERATED",
        priority: "1",
        name,
        documentUrl,
      });

      queryClient.invalidateQueries({ queryKey: ["patientDocuments"] });

      return { id: documentId, name, documentType: RECEIPTS_DOCUMENT_TYPE_NAME };
    },
  });
};
